/**
 * ROBO-478. The API's refusal to start or continue a session because the caller's business is
 * suspended.
 *
 * `Auth/login`, `Auth/authorize` and `auth/refresh-token` answer a refused user — a suspended
 * business's team member, client, group admin or assistant — with HTTP 403 and the standard
 * `ApiResultPayload` body `{ "code": "BRAND_SUSPENDED", "status": 403 }` (no `title`). An Owner of
 * a suspended business is NOT refused: they get a session with `suspended: true` instead, and an
 * Admin is never affected. Legacy parity: `Login/login.asp:75-83`.
 *
 * ★ A 403 ALONE IS NOT A SUSPENSION. `auth/refresh-token` already answers a bare 403 for an invalid
 * or expired refresh token, and that must keep taking the ordinary terminal-refresh path. Only the
 * body's `code` distinguishes the two, so it is the only thing checked.
 */
export const BRAND_SUSPENDED_CODE = 'BRAND_SUSPENDED';

/** Why the server refused a session. One reason today; a union so more can be added. */
export type SessionRefusalReason = 'brand-suspended';

function asRecord(value: unknown): Record<string, unknown> | undefined {
    return value !== null && typeof value === 'object'
        ? (value as Record<string, unknown>)
        : undefined;
}

/**
 * Whether `error` is the API's BRAND_SUSPENDED refusal.
 *
 * `services/http.ts` throws `HttpError` with the parsed JSON body as `cause`. Read structurally
 * rather than with `instanceof`, and every field type-checked, because a proxy or an older API can
 * put anything in that body (HTML, an array, nothing) — none of which may be taken for a refusal.
 */
export function isBrandSuspendedError(error: unknown): boolean {
    const err = asRecord(error);
    if (!err || err.status !== 403) {
        return false;
    }
    return asRecord(err.cause)?.code === BRAND_SUSPENDED_CODE;
}
